import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import {
  ArrowLeft,
  MessageSquare,
  Bell,
  ChevronDown,
  ChevronUp,
  Clock,
} from 'lucide-react';

interface InboxMessage {
  id: string;
  title: string;
  body: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  sender_name: string;
  read: boolean;
  created_at: string;
}

const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'border-l-gray-400',
  NORMAL: 'border-l-blue-500',
  HIGH: 'border-l-orange-500',
  URGENT: 'border-l-red-500',
};

const PRIORITY_DOT_COLORS: Record<string, string> = {
  LOW: 'bg-gray-400',
  NORMAL: 'bg-blue-500',
  HIGH: 'bg-orange-500',
  URGENT: 'bg-red-500',
};

function formatMessageDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
}

export default function Messages() {
  const [, setLocation] = useLocation();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data: messages, isLoading, error } = useQuery<InboxMessage[]>({
    queryKey: ['messages', 'inbox'],
    queryFn: () => api.get('/messages/inbox').then((res) => res.data.data),
  });

  const markAsRead = useMutation({
    mutationFn: (messageId: string) =>
      api.patch(`/messages/${messageId}/read`).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', 'inbox'] });
    },
  });

  const handleToggleMessage = (msg: InboxMessage) => {
    if (expandedId === msg.id) {
      setExpandedId(null);
    } else {
      setExpandedId(msg.id);
      if (!msg.read) {
        markAsRead.mutate(msg.id);
      }
    }
  };

  const unreadCount = messages?.filter((m) => !m.read).length || 0;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation('/')}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Avisos</h1>
            {unreadCount > 0 && (
              <p className="text-sm opacity-80">
                {unreadCount} {unreadCount === 1 ? 'nao lido' : 'nao lidos'}
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <div className="relative">
              <Bell className="h-6 w-6" />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando avisos...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-sm text-red-700">Erro ao carregar avisos. Tente novamente.</p>
            </div>
          </div>
        ) : !messages || messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <MessageSquare className="h-16 w-16 text-gray-300 mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-1">Nenhum aviso</h2>
            <p className="text-sm text-gray-500 text-center">
              Voce nao tem avisos no momento. Novos avisos aparecerão aqui.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => {
              const isExpanded = expandedId === msg.id;
              const borderColor = PRIORITY_COLORS[msg.priority] || 'border-l-gray-400';
              const dotColor = PRIORITY_DOT_COLORS[msg.priority] || 'bg-gray-400';

              return (
                <div
                  key={msg.id}
                  onClick={() => handleToggleMessage(msg)}
                  className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer transition-all active:bg-gray-50 border-l-4 ${borderColor}`}
                >
                  <div className="p-4">
                    {/* Header Row */}
                    <div className="flex items-start gap-3">
                      {/* Unread dot */}
                      <div className="flex-shrink-0 mt-1.5">
                        {!msg.read ? (
                          <div className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
                        ) : (
                          <div className="w-2.5 h-2.5" />
                        )}
                      </div>

                      {/* Message Info */}
                      <div className="flex-1 min-w-0">
                        <h3
                          className={`text-sm truncate ${
                            !msg.read ? 'font-bold text-gray-900' : 'font-normal text-gray-700'
                          }`}
                        >
                          {msg.title}
                        </h3>

                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">{msg.sender_name}</span>
                          <span className="text-xs text-gray-300">|</span>
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <Clock className="h-3 w-3" />
                            {formatMessageDate(msg.created_at)}
                          </div>
                        </div>

                        {/* Body Preview (when collapsed) */}
                        {!isExpanded && (
                          <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                            {msg.body}
                          </p>
                        )}

                        {/* Full Body (when expanded) */}
                        {isExpanded && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {msg.body}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Expand Icon */}
                      <div className="flex-shrink-0 text-gray-400">
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
